/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState } from "react";
import { useApolloClient } from "@apollo/react-hooks";
import { reportStatistics } from "./queries";
import { Segment, Accordion } from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import { authCheckCallApi } from "../../Authenticator/utils";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import UsersList from "./UsersList";
import ReportParams from "./ReportParams";
import ConfirmationDialog from "../../ConfirmationDialog";
import { Ico } from "../../Icon/Icon";
import PlanUpdates from "./PlanUpdates";
import FreeTrials from "./FreeTrials";

interface ReportsProps {
  ro: boolean;
}

export const defaultStartDate = () => {
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  return startDate;
};

export const defaultEndDate = () => {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  return endDate;
};

const Reports: React.FC<ReportsProps> = ({ ro }) => {
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [startDate, setStartDate] = useState<Date>(defaultStartDate());
  const [endDate, setEndDate] = useState<Date>(defaultEndDate());

  const client = useApolloClient();
  const [reportData, setReportData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);

  const [showSignups, setShowSignups] = useState<boolean>(true);
  const [showPlanUpdates, setShowPlanUpdates] = useState<boolean>(true);
  const [showFreeTrials, setShowFreeTrials] = useState<boolean>(true);

  const generateReport = (fixedStartDate?: Date, fixedEndDate?: Date) => {
    setLoading(true);
    startDate.setHours(0);
    startDate.setMinutes(0);
    authCheckCallApi(() => {
      client
        .query({
          fetchPolicy: "no-cache",
          query: reportStatistics,
          variables: {
            options: {
              start: fixedStartDate || startDate,
              end: fixedEndDate || endDate,
              reportType: "signup-statistics",
            },
          },
        })
        .then((result) => {
          setReportData(JSON.parse(result.data.res.result));
        })
        .catch((e) => {
          setMessage({
            title: "Report",
            message: e.message,
          });
        })
        .finally(() => {
          setLoading(false);
          setShowSignups(true);
          setShowPlanUpdates(true);
          setShowFreeTrials(true);
        });
    });
  };

  const reportContent = () => {
    return (
      <Accordion>
        <Accordion.Title
          className={styles.advancedTitle}
          active={showSignups}
          index={1}
          onClick={() => {
            setShowSignups(!showSignups);
          }}
        >
          <Ico
            name={showSignups === true ? "down" : "right"}
            className={styles.accordionIcon}
          />
          Signups
        </Accordion.Title>
        <Accordion.Content active={showSignups}>
          <UsersList
            usersData={reportData?.signups}
            ro={ro}
            hiddenColumns={["lastLogin", "role"]}
          />
        </Accordion.Content>

        <Accordion.Title
          className={styles.advancedTitle}
          active={showPlanUpdates}
          index={3}
          onClick={() => {
            setShowPlanUpdates(!showPlanUpdates);
          }}
        >
          <Ico
            name={showPlanUpdates === true ? "down" : "right"}
            className={styles.accordionIcon}
          />
          Plan Updates
        </Accordion.Title>
        <Accordion.Content active={showPlanUpdates}>
          <PlanUpdates data={reportData?.planUpdates} />
        </Accordion.Content>

        <Accordion.Title
          className={styles.advancedTitle}
          active={showFreeTrials}
          index={3}
          onClick={() => {
            setShowFreeTrials(!showFreeTrials);
          }}
        >
          <Ico
            name={showFreeTrials === true ? "down" : "right"}
            className={styles.accordionIcon}
          />
          Free Trials
        </Accordion.Title>
        <Accordion.Content active={showFreeTrials}>
          <FreeTrials data={reportData?.trialData} />
        </Accordion.Content>
      </Accordion>
    );
  };

  return (
    <Segment basic>
      <ReportParams
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        loading={loading}
        generateReport={generateReport}
      />

      {reportData && reportContent()}

      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </Segment>
  );
};

export default Reports;
